import { isValidBarcode, isValidHSCode } from '../helpers'
import {
  countryConfigs,
  europeanUnionCountryCodes,
  europeanUnionCustomsConfig,
} from './ExportPrices/CountryExtraFields/constants'
import { europeanUnionBlock } from './constants'

export const isValidProductLocations = (productLocations = []) => {
  return productLocations.every(pl => {
    const isMissingLocation = pl.details && !pl.location_id
    return !isMissingLocation
  })
}

export const isValidCustomsCode = (customsCode, hsCode, extraDigits = 4) => {
  if (extraDigits === 0) return true
  if (!customsCode) return false
  const suffix = hsCode && customsCode.startsWith(hsCode) ? customsCode.slice(hsCode.length) : customsCode
  return suffix.length === extraDigits
}

const getRegionName = (regions, code) => regions.find(region => region.code === code)?.name

const mapCountryBlock = (code, customsRequirements, regions) => {
  const isEuropeanUnionCountry = europeanUnionCountryCodes.includes(code)
  const hasCustoms = customsRequirements.customs_country_codes.includes(code)
  const showCustomsCode = !isEuropeanUnionCountry && hasCustoms && !!countryConfigs[code]

  return {
    code,
    name: getRegionName(regions, code),
    isEuropeanUnion: false,
    showCustomsCode,
    showExportPrice: customsRequirements.importer_subsidiary_country_codes.includes(code),
    showManufacturer: code === 'US' && showCustomsCode,
  }
}

export const getExportBlocks = ({ customsRequirements, regions }) => {
  if (!customsRequirements) return []

  const countryCodes = new Set([
    ...customsRequirements.customs_country_codes,
    ...customsRequirements.importer_subsidiary_country_codes,
  ])

  const countryBlocks = [...countryCodes]
    .map(code => mapCountryBlock(code, customsRequirements, regions))
    .filter(block => block.showCustomsCode || block.showExportPrice)

  if (!customsRequirements.needs_ns_pid) return countryBlocks

  return [europeanUnionBlock, ...countryBlocks]
}

const isValidOptionalCustomsCode = (customsCode, hsCode, extraDigits) => {
  if (!hsCode || !customsCode || customsCode === hsCode) return true

  return isValidCustomsCode(customsCode, hsCode, extraDigits)
}

export const isValidExportPrices = (exportPrices, hsCode, exportBlocks) => {
  if (!exportPrices?.length) return true

  const customsCodeCountries = exportBlocks
    .filter(block => !block.isEuropeanUnion && block.showCustomsCode)
    .map(block => block.code)

  return exportPrices.every(ep => {
    const countryCode = ep.country_code || ep.country?.code

    if (!customsCodeCountries.includes(countryCode)) return true

    return isValidOptionalCustomsCode(ep.country_customs_code, hsCode, countryConfigs[countryCode].extraDigits)
  })
}

export const isValidTaricCode = (taricCode, hsCode, exportBlocks) => {
  if (!exportBlocks.some(block => block.isEuropeanUnion)) return true

  return isValidOptionalCustomsCode(taricCode, hsCode, europeanUnionCustomsConfig.extraDigits)
}

export const getCustomsCountryNames = ({ customsRequirements, regions, europeanUnionName }) => {
  if (!customsRequirements) return []

  const countryNames = customsRequirements.customs_country_codes
    .filter(code => !europeanUnionCountryCodes.includes(code))
    .map(code => getRegionName(regions, code))
    .filter(Boolean)

  if (!customsRequirements.needs_ns_pid) return countryNames

  return [europeanUnionName, ...countryNames]
}

export const formatCountryList = (countryNames, language) => {
  return new Intl.ListFormat(language, { style: 'long', type: 'conjunction' }).format(countryNames)
}

export const isFormValid = (item, exportBlocks) => {
  return (
    (!item.hs_code || isValidHSCode(item.hs_code)) &&
    (!Number(item.weight) || Boolean(item.weight_unit)) &&
    (!item.barcode || isValidBarcode(item.barcode)) &&
    isValidProductLocations(item.product_locations) &&
    isValidExportPrices(item.export_details, item.hs_code, exportBlocks) &&
    isValidTaricCode(item.taric_code, item.hs_code, exportBlocks)
  )
}
